// Accounting — Receivables / Payables drill-down (read-only)
//
// Uses the same ageing query as the reports, but focuses on the parties and
// their outstanding invoices. Recording receipts/payments is a follow-up
// (needs the server-side receipt RPCs).

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import type { AccountingMeta, AgingInvoice, AgingResult } from '../lib/api.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

const money = (n: number | undefined) => (Number(n) || 0).toFixed(2);

function todayIso(): string {
  const d = new Date();
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
}
function isoToMs(v: string, endOfDay = false): number {
  if (!v) return 0;
  const d = new Date(`${v}T00:00:00`);
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return d.getTime();
}
function msToIso(ms: number): string {
  if (!ms) return todayIso();
  const d = new Date(ms);
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
}

type LedgerKind = 'receivables' | 'payables';

@customElement('aging-panel')
export class AgingPanel extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .bar { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: end; margin: 0.75rem 1rem 0.5rem; flex-shrink: 0; }
    .bar .fg { margin: 0; }
    .bar input { min-height: 38px; padding: 7px 10px; border: 1px solid var(--color-border); border-radius: 10px; background: var(--color-surface); color: var(--color-text); font-family: inherit; font-size: 0.84rem; }
    .seg { display: flex; border: 1px solid var(--color-border); border-radius: 10px; overflow: hidden; }
    .seg button { border: none; background: var(--color-surface); padding: 0 14px; min-height: 38px; font-family: inherit; font-size: 0.82rem; font-weight: 600; cursor: pointer; color: var(--color-text-secondary); }
    .seg button.active { background: var(--color-primary); color: #fff; }
    .body { flex: 1; min-height: 0; overflow: auto; padding: 0 1rem 1rem; }
    .wrap { border: 1px solid var(--color-border); border-radius: 12px; overflow: auto; background: var(--color-surface); margin-bottom: 1rem; }
    tfoot td { font-weight: 700; background: #f8fafd; }
    .state { padding: 2rem; text-align: center; color: var(--color-text-secondary); }
    .error { margin: 0 1rem 0.5rem; padding: 0.6rem 0.8rem; border-radius: 10px; background: #fdecea; color: #b71c1c; font-size: 0.84rem; }
    .muted { color: var(--color-text-secondary); font-size: 0.78rem; }
    .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); display: none; align-items: center; justify-content: center; z-index: 60; padding: 1rem; }
    .modal-overlay.open { display: flex; }
    .modal { width: min(760px, 100%); max-height: 90vh; overflow: auto; background: var(--color-surface); border-radius: 16px; }
    .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 1.1rem; border-bottom: 1px solid var(--color-border); }
    .modal-header h3 { margin: 0; font-size: 1rem; }
    .modal-body { padding: 1rem 1.1rem; }
    .modal-footer { display: flex; justify-content: flex-end; gap: 0.5rem; padding: 0.8rem 1.1rem; border-top: 1px solid var(--color-border); }
    .close { border: none; background: none; font-size: 1.35rem; cursor: pointer; color: var(--color-text-secondary); }
  `];

  @property({ type: String }) storeCode = '';

  @state() private _kind: LedgerKind = 'receivables';
  @state() private _asOf = todayIso();
  @state() private _data: AgingResult | null = null;
  @state() private _party = 0;
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _meta: AccountingMeta | null = null;
  @state() private _saving = false;
  @state() private _formError = '';
  @state() private _form: {
    partyId: number;
    paidById: string;
    date: number;
    reference: string;
    remark: string;
    amounts: Record<string, string>;
  } | null = null;

  private _view = new OutletViewCache<{ kind: LedgerKind; asOf: string; party: number }>(this, 'accounting-aging', {
    capture: () => ({ kind: this._kind, asOf: this._asOf, party: this._party }),
    restore: (v) => { this._kind = v.kind; this._asOf = v.asOf; this._party = v.party; },
    reset: () => { this._kind = 'receivables'; this._asOf = todayIso(); this._party = 0; this._data = null; },
  });

  connectedCallback() {
    super.connectedCallback();
    // No loads here: updated() drives them from the switchTo() result, so a
    // load started now would be duplicated on the first update.
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this._view.dispose();
  }
  protected updated(changed: Map<PropertyKey, unknown>) {
    // switchTo() returns 'unchanged' | 'restored' | 'fresh' — compare it, never
    // treat it as a boolean: every one of those strings is truthy, and a
    // truthy check here re-runs the loaders on every render (endless loop).
    const switched = this._view.switchTo(this.storeCode);
    if (switched === 'unchanged' && !changed.has('storeCode')) return;
    this._meta = null;
    // The ageing payload is not part of the kept snapshot, so it must be
    // refetched for the outlet being entered (never show the previous one).
    this._data = null;
    void this._loadMeta();
    void this._load();
  }

  private async _loadMeta() {
    if (!this.storeCode) return;
    const epoch = this._view.epoch;
    try {
      const res = await api.accountingMeta(this.storeCode);
      if (this._view.isCurrent(epoch)) this._meta = res.data;
    } catch { /* surfaced by _load */ }
  }

  private _payByOptions() {
    const all = this._meta?.payBy ?? [];
    // Receipts may use any money account; supplier payments only cash/bank.
    return this._kind === 'receivables'
      ? all.filter((p) => p.kind !== 'receivable')
      : all.filter((p) => p.kind === 'cash' || p.kind === 'bank');
  }

  private _partyInvoices(): AgingInvoice[] {
    if (!this._form) return [];
    return (this._data?.invoices ?? []).filter((i) => i.partyId === this._form!.partyId);
  }

  private _openForm() {
    const parties = this._data?.parties ?? [];
    const partyId = this._party || parties[0]?.partyId || 0;
    const payBy = this._payByOptions();
    this._form = {
      partyId,
      paidById: payBy[0]?.id ?? '',
      date: isoToMs(todayIso(), false),
      reference: '',
      remark: '',
      amounts: {},
    };
    this._formError = '';
  }

  private _setForm<K extends keyof NonNullable<typeof this._form>>(
    key: K,
    value: NonNullable<typeof this._form>[K],
  ) {
    if (!this._form) return;
    this._form = { ...this._form, [key]: value };
  }

  private _pickFormParty(id: number) {
    if (!this._form) return;
    this._form = { ...this._form, partyId: id, amounts: {} };
  }

  private _formTotal(): number {
    if (!this._form) return 0;
    return this._partyInvoices().reduce((s, i) => {
      const v = parseFloat(this._form!.amounts[i.id] ?? '') || 0;
      return s + Math.max(0, v);
    }, 0);
  }

  private async _savePayment() {
    const f = this._form;
    if (!f) return;
    const allocations = this._partyInvoices()
      .map((i) => ({
        invoiceId: i.id,
        amount: parseFloat(f.amounts[i.id] ?? '') || 0,
      }))
      .filter((a) => a.amount > 0);
    if (!f.partyId) { this._formError = 'Select a party.'; return; }
    if (!f.paidById) { this._formError = 'Select how the money was paid.'; return; }
    if (!allocations.length) { this._formError = 'Enter an amount against at least one invoice.'; return; }
    this._saving = true;
    this._formError = '';
    const epoch = this._view.epoch;
    try {
      const body = {
        partyId: String(f.partyId),
        paidById: f.paidById,
        date: f.date,
        reference: f.reference,
        remark: f.remark,
        allocations,
      };
      if (this._kind === 'receivables') await api.accountingRecordReceipt(this.storeCode, body);
      else await api.accountingRecordPayment(this.storeCode, body);
      if (!this._view.isCurrent(epoch)) return;
      this._form = null;
      await this._load();
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) this._formError = e?.message || 'Could not record the payment';
    } finally {
      if (this._view.isCurrent(epoch)) this._saving = false;
    }
  }

  private _renderForm() {
    const f = this._form!;
    const parties = this._data?.parties ?? [];
    const invoices = this._partyInvoices();
    const payBy = this._payByOptions();
    const total = this._formTotal();
    return html`
      <div class="modal-overlay open" @click=${(ev: Event) => { if (ev.target === ev.currentTarget) this._form = null; }}>
        <div class="modal" @click=${(ev: Event) => ev.stopPropagation()}>
          <div class="modal-header">
            <h3>${this._kind === 'receivables' ? 'Record customer receipt' : 'Record supplier payment'}</h3>
            <button class="close" @click=${() => (this._form = null)}>&times;</button>
          </div>
          <div class="modal-body">
            ${this._formError ? html`<div class="error" style="margin:0 0 0.7rem">${this._formError}</div>` : ''}
            <div class="grid">
              <div class="fg">
                <label>${this._kind === 'receivables' ? 'Customer' : 'Supplier'}</label>
                <select .value=${String(f.partyId)}
                  @change=${(ev: Event) => this._pickFormParty(Number((ev.target as HTMLSelectElement).value))}>
                  <option value="0">Select…</option>
                  ${parties.map((p) => html`<option value=${p.partyId} ?selected=${f.partyId === p.partyId}>${p.partyName || `#${p.partyId}`}</option>`)}
                </select>
              </div>
              <div class="fg">
                <label>Paid by</label>
                <select .value=${f.paidById} @change=${(ev: Event) => this._setForm('paidById', (ev.target as HTMLSelectElement).value)}>
                  <option value="">Select…</option>
                  ${payBy.map((p) => html`<option value=${p.id} ?selected=${f.paidById === p.id}>${p.name}</option>`)}
                </select>
              </div>
              <div class="fg">
                <label>Date</label>
                <input type="date" .value=${msToIso(f.date)}
                  @change=${(ev: Event) => this._setForm('date', isoToMs((ev.target as HTMLInputElement).value))}>
              </div>
              <div class="fg">
                <label>Reference</label>
                <input .value=${f.reference} @input=${(ev: Event) => this._setForm('reference', (ev.target as HTMLInputElement).value)}>
              </div>
              <div class="fg full">
                <label>Remark</label>
                <textarea .value=${f.remark} @input=${(ev: Event) => this._setForm('remark', (ev.target as HTMLTextAreaElement).value)}></textarea>
              </div>
            </div>

            <h4 style="margin:0.9rem 0 0.4rem;font-size:0.84rem">Allocate to invoices (incl. GST)</h4>
            <div class="wrap">
              <table>
                <thead><tr><th>Invoice</th><th>Due</th><th class="num">Outstanding</th><th class="num">Pay now</th></tr></thead>
                <tbody>
                  ${invoices.map((i) => html`<tr>
                    <td>${i.refNo || i.id}</td>
                    <td>${msToIso(i.dueDate)}</td>
                    <td class="num">${money(i.outstanding)}</td>
                    <td class="num">
                      <input type="number" step="0.01" min="0" max=${i.outstanding} style="width:120px"
                        placeholder=${money(i.outstanding)}
                        .value=${f.amounts[i.id] ?? ''}
                        @input=${(ev: Event) => {
                          const v = (ev.target as HTMLInputElement).value;
                          this._form = { ...this._form!, amounts: { ...this._form!.amounts, [i.id]: v } };
                        }}>
                    </td>
                  </tr>`)}
                  ${!invoices.length ? html`<tr><td colspan="4" class="state">Select a party with outstanding invoices.</td></tr>` : ''}
                </tbody>
                ${invoices.length ? html`<tfoot><tr><td colspan="3">Total</td><td class="num">${money(total)}</td></tr></tfoot>` : ''}
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click=${() => (this._form = null)}>Cancel</button>
            <button class="btn-submit" ?disabled=${this._saving || total <= 0}
              @click=${() => void this._savePayment()}>
              ${this._saving ? 'Posting…' : `Post ${money(total)}`}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private async _load() {
    if (!this.storeCode) return;
    const epoch = this._view.epoch;
    this._loading = true;
    this._error = '';
    try {
      const res = await api.accountingAging(this.storeCode, this._kind, {
        from: 0,
        to: isoToMs(this._asOf, true),
        asOf: isoToMs(this._asOf, true),
      });
      if (!this._view.isCurrent(epoch)) return;
      this._data = res.data;
    } catch (e: any) {
      if (this._view.isCurrent(epoch)) this._error = e?.message || 'Failed to load ageing';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  render() {
    if (!this.storeCode) return html`<div class="state">No outlet selected.</div>`;
    const d = this._data;
    const invoices = (d?.invoices ?? []).filter((i) => !this._party || i.partyId === this._party);
    return html`
      <div class="bar">
        <div class="seg">
          <button class=${this._kind === 'receivables' ? 'active' : ''}
            @click=${() => { this._kind = 'receivables'; this._party = 0; void this._load(); }}>Customers</button>
          <button class=${this._kind === 'payables' ? 'active' : ''}
            @click=${() => { this._kind = 'payables'; this._party = 0; void this._load(); }}>Suppliers</button>
        </div>
        <div class="fg"><label>As at</label>
          <input type="date" .value=${this._asOf} @change=${(ev: Event) => { this._asOf = (ev.target as HTMLInputElement).value; void this._load(); }}></div>
        ${this._party ? html`<button class="btn-sec btn" @click=${() => (this._party = 0)}>✕ Clear party filter</button>` : ''}
        <button class="btn" @click=${() => void this._load()} ?disabled=${this._loading}>↻ Refresh</button>
        <button class="btn" ?disabled=${!this._data?.parties.length} @click=${() => this._openForm()}>
          ${this._kind === 'receivables' ? '💰 Record receipt' : '💸 Record payment'}
        </button>
      </div>
      ${this._error ? html`<div class="error">${this._error}</div>` : ''}
      <div class="body">
        <div class="wrap">
          <table>
            <thead><tr><th>${this._kind === 'receivables' ? 'Customer' : 'Supplier'}</th>
              <th class="num">Current</th><th class="num">1–30</th><th class="num">31–60</th>
              <th class="num">61–90</th><th class="num">90+</th><th class="num">Total</th></tr></thead>
            <tbody>
              ${(d?.parties ?? []).map((p) => html`<tr class="clickable" style=${this._party === p.partyId ? 'background:#eef4ff' : ''}
                @click=${() => { this._party = this._party === p.partyId ? 0 : p.partyId; }}>
                <td>${p.partyName || `#${p.partyId}`}</td>
                <td class="num">${money(p.current)}</td><td class="num">${money(p.d1_30)}</td>
                <td class="num">${money(p.d31_60)}</td><td class="num">${money(p.d61_90)}</td>
                <td class="num">${money(p.d90p)}</td><td class="num"><strong>${money(p.total)}</strong></td>
              </tr>`)}
              ${!(d?.parties ?? []).length ? html`<tr><td colspan="7" class="state">Nothing outstanding.</td></tr>` : ''}
            </tbody>
            ${(d?.parties ?? []).length ? html`<tfoot><tr><td>Total</td>
              <td class="num">${money(d!.parties.reduce((s, p) => s + p.current, 0))}</td>
              <td class="num">${money(d!.parties.reduce((s, p) => s + p.d1_30, 0))}</td>
              <td class="num">${money(d!.parties.reduce((s, p) => s + p.d31_60, 0))}</td>
              <td class="num">${money(d!.parties.reduce((s, p) => s + p.d61_90, 0))}</td>
              <td class="num">${money(d!.parties.reduce((s, p) => s + p.d90p, 0))}</td>
              <td class="num">${money(d!.parties.reduce((s, p) => s + p.total, 0))}</td></tr></tfoot>` : ''}
          </table>
        </div>

        <div class="wrap">
          <table>
            <thead><tr><th>${this._kind === 'receivables' ? 'Customer' : 'Supplier'}</th><th>Ref</th>
              <th>Date</th><th>Due</th><th class="num">Amount (incl. GST)</th><th class="num">Paid</th>
              <th class="num">Outstanding</th><th class="num">Overdue days</th></tr></thead>
            <tbody>
              ${invoices.map((i) => html`<tr>
                <td>${i.partyName || `#${i.partyId}`}</td><td>${i.refNo || i.id}</td>
                <td>${msToIso(i.date)}</td><td>${msToIso(i.dueDate)}</td>
                <td class="num">${money(i.invoiceAmount)}</td><td class="num">${money(i.paid)}</td>
                <td class="num">${money(i.outstanding)}</td>
                <td class="num">${Math.max(0, Math.round(i.daysOverdue))}</td>
              </tr>`)}
              ${!invoices.length ? html`<tr><td colspan="8" class="state">No outstanding invoices.</td></tr>` : ''}
            </tbody>
          </table>
        </div>
        <div class="muted">Amounts include GST (payments are actual cash, so both sides are gross).
          Paid = non-credit sales receipts (or supplier payments) plus legacy journal allocations;
          PWA receipts are written to both ledgers so they are counted once.</div>
      </div>
      ${this._form ? this._renderForm() : ''}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'aging-panel': AgingPanel;
  }
}
